import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ImagePlus, Sparkles, Upload, Trash2, X, User,
  Wand2, RotateCcw, Plus, Check, Pencil, Users, Video, Maximize2, ZoomIn,
  ChevronLeft, ChevronRight, Home, Package,
} from "lucide-react";

import { BringToLifeModal } from "@/components/BringToLifeModal";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useVisionBoard, ReferencePhoto, ReferenceKind } from "@/hooks/useVisionBoard";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { saveSelectedChatBackground } from "@/utils/chatBackdropStorage";


// ─── Kind picker (segmented control) ─────────────────────────────────────────
const KIND_META: Record<ReferenceKind, { label: string; Icon: typeof User }> = {
  person: { label: "Person", Icon: User },
  place: { label: "Place", Icon: Home },
  object: { label: "Object", Icon: Package },
};

const KindSegmented: React.FC<{
  value: ReferenceKind;
  onChange: (k: ReferenceKind) => void;
  size?: "sm" | "xs";
}> = ({ value, onChange, size = "sm" }) => (
  <div className="inline-flex rounded-md border border-border bg-secondary/30 p-0.5">
    {(Object.keys(KIND_META) as ReferenceKind[]).map((k) => {
      const { label, Icon } = KIND_META[k];
      const active = value === k;
      return (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={`flex items-center gap-1 rounded px-1.5 ${size === "xs" ? "py-0.5 text-[10px]" : "py-1 text-xs"} transition-colors ${
            active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Icon className={size === "xs" ? "h-3 w-3" : "h-3.5 w-3.5"} />
          {label}
        </button>
      );
    })}
  </div>
);

// ─── Reference photos popover ────────────────────────────────────────────────
interface RefPhotosPopoverProps {
  referencePhotos: ReferencePhoto[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onAdd: (file: File, label: string, kind: ReferenceKind) => void;
  onRemove: (id: string) => void;
  onRenameStart: (ref: ReferencePhoto) => void;
  onChangeKind: (id: string, kind: ReferenceKind) => void;
}

const RefPhotosPopover: React.FC<RefPhotosPopoverProps> = ({
  referencePhotos, selectedIds, onToggle, onAdd, onRemove, onRenameStart, onChangeKind,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingLabel, setPendingLabel] = useState("");
  const [pendingKind, setPendingKind] = useState<ReferenceKind>("person");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) { setPendingFile(file); setPendingLabel(""); setPendingKind("person"); }
    e.target.value = "";
  };

  const handleConfirmAdd = () => {
    if (!pendingFile) return;
    onAdd(pendingFile, pendingLabel || "Reference", pendingKind);
    setPendingFile(null);
    setPendingLabel("");
    setPendingKind("person");
  };

  return (
    <div className="w-80 p-3 space-y-3">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Reference Photos
      </p>

      {referencePhotos.length === 0 && !pendingFile && (
        <p className="text-xs text-muted-foreground text-center py-2">
          No reference photos yet. Add one below.
        </p>
      )}

      {/* Existing references */}
      <div className="space-y-2">
        {referencePhotos.map((ref) => {
          const isSelected = selectedIds.includes(ref.id);
          return (
            <div
              key={ref.id}
              className="flex flex-col gap-1.5 rounded-lg border border-border bg-secondary/30 px-2 py-1.5"
            >
              <div className="flex items-center gap-2">
                {/* Avatar */}
                <div className="relative shrink-0">
                  <img
                    src={ref.url}
                    alt={ref.label}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                  {isSelected && (
                    <div className="absolute inset-0 rounded-full bg-primary/60 flex items-center justify-center">
                      <Check className="h-3.5 w-3.5 text-white" />
                    </div>
                  )}
                </div>

                {/* Label */}
                <span className="flex-1 text-sm text-foreground truncate">{ref.label}</span>

                <button
                  onClick={() => onRenameStart(ref)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="Rename"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => onRemove(ref.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  title="Remove"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => onToggle(ref.id)}
                  className={`text-xs px-1.5 py-0.5 rounded-md border transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                  }`}
                >
                  {isSelected ? "On" : "Off"}
                </button>
              </div>
              <div className="pl-10">
                <KindSegmented value={ref.kind} onChange={(k) => onChangeKind(ref.id, k)} size="xs" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Pending new reference */}
      {pendingFile && (
        <div className="space-y-2 border border-border rounded-lg p-2 bg-secondary/20">
          <div className="flex items-center gap-2">
            <img
              src={URL.createObjectURL(pendingFile)}
              alt="preview"
              className="h-10 w-10 rounded-full object-cover shrink-0"
            />
            <Input
              autoFocus
              placeholder='Label, e.g. "My home"'
              value={pendingLabel}
              onChange={(e) => setPendingLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleConfirmAdd()}
              className="h-8 text-sm"
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Type</span>
            <KindSegmented value={pendingKind} onChange={setPendingKind} size="xs" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 h-7 text-xs cosmic-button" onClick={handleConfirmAdd}>
              Add
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPendingFile(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Add button */}
      {!pendingFile && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2 h-8 text-xs"
            onClick={() => fileInputRef.current?.click()}
          >
            <Plus className="h-3.5 w-3.5" /> Add Reference Photo
          </Button>
        </>
      )}

      {selectedIds.length > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {selectedIds.length} reference{selectedIds.length > 1 ? "s" : ""} will be included in AI generations
        </p>
      )}
    </div>
  );
};

// ─── Rename dialog ────────────────────────────────────────────────────────────
interface RenameDialogProps {
  ref: ReferencePhoto | null;
  onSave: (id: string, label: string) => void;
  onClose: () => void;
}

const RenameDialog: React.FC<RenameDialogProps> = ({ ref, onSave, onClose }) => {
  const [value, setValue] = useState(ref?.label ?? "");

  if (!ref) return null;

  const handleSave = () => {
    if (value.trim()) { onSave(ref.id, value.trim()); }
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="cosmic-card p-5 w-full max-w-xs space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <img src={ref.url} alt={ref.label} className="h-10 w-10 rounded-full object-cover" />
          <div>
            <p className="text-sm font-medium text-foreground">Rename reference</p>
            <p className="text-xs text-muted-foreground truncate max-w-[160px]">{ref.label}</p>
          </div>
        </div>
        <Input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") onClose(); }}
          placeholder='e.g. "My son"'
          className="text-sm"
        />
        <div className="flex gap-2">
          <Button size="sm" className="flex-1 cosmic-button" onClick={handleSave}>Save</Button>
          <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const VisionBoard = () => {
  const {
    images, isUploading, isGenerating, isAssisting,
    referencePhotos, addReferencePhoto, updateReferenceLabel, updateReferenceKind, removeReferencePhoto,
    generateImage, assistPrompt, removeImage, uploadImages, saveVideo, expandImage, upscaleImage,
  } = useVisionBoard();


  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const previewItem = previewIndex !== null && images[previewIndex]
    ? { url: images[previewIndex].url, type: images[previewIndex].type }
    : null;
  const [selectedRefIds, setSelectedRefIds] = useState<string[]>([]);

  // Auto-select all reference photos when they load
  useEffect(() => {
    if (referencePhotos.length > 0 && selectedRefIds.length === 0) {
      setSelectedRefIds(referencePhotos.map((r) => r.id));
    }
  }, [referencePhotos]);
  const [renamingRef, setRenamingRef] = useState<ReferencePhoto | null>(null);
  const [refPopoverOpen, setRefPopoverOpen] = useState(false);
  const [bringToLifeImage, setBringToLifeImage] = useState<{ id: string; url: string; name: string } | null>(null);
  const [isExpanding, setIsExpanding] = useState(false);
  const [isUpscaling, setIsUpscaling] = useState(false);

  const handleUpscale = async (id: string, url: string, name: string) => {
    setIsUpscaling(true);
    try {
      await upscaleImage(id, url, name);
    } finally {
      setIsUpscaling(false);
    }
  };

  const handleExpand = async (id: string, url: string, name: string) => {
    setIsExpanding(true);
    try {
      await expandImage(id, url, name);
    } finally {
      setIsExpanding(false);
    }
  };


  const handleAnimate = async (imageUrl: string, motionBucket: number, prompt: string): Promise<string | null> => {
    // Step 1: Start the prediction
    const { data: startData, error: startError } = await supabase.functions.invoke("animate-image", {
      body: { imageUrl, motionBucket, prompt },
    });
    if (startError) throw new Error(startError.message || "Failed to start animation");
    if (startData?.error) throw new Error(startData.error);

    const predictionId = startData?.predictionId;
    if (!predictionId) throw new Error("No prediction ID returned from server");

    // Step 2: Poll until done (up to ~3 minutes)
    const maxAttempts = 90; // 90 × 2s = 3 min
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 2000));

      const { data: pollData, error: pollError } = await supabase.functions.invoke("animate-image-poll", {
        body: { predictionId },
      });

      if (pollError) {
        console.warn("Poll error (will retry):", pollError);
        continue;
      }

      if (pollData?.status === "succeeded" && pollData?.url) {
        return pollData.url;
      }

      if (pollData?.status === "failed" || pollData?.status === "canceled") {
        throw new Error(pollData?.error || "Video generation failed on the server");
      }

      // status is "starting" or "processing" — keep polling
    }

    throw new Error("Video generation timed out after 3 minutes");
  };


  const onDrop = useCallback((acceptedFiles: File[]) => { uploadImages(acceptedFiles); }, [uploadImages]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/webp": [".webp"],
      "image/gif": [".gif"],
      "video/mp4": [".mp4"],
      "video/webm": [".webm"],
      "video/quicktime": [".mov"],
    },
    multiple: true,
  });

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    const selected = referencePhotos
      .filter((r) => selectedRefIds.includes(r.id))
      .map((r) => ({ url: r.url, label: r.label, kind: r.kind }));
    await generateImage(prompt.trim(), selected);
    setPrompt("");
    setShowAIPrompt(false);
  };

  const handleAssist = async () => {
    if (!prompt.trim()) return;
    const improved = await assistPrompt(prompt.trim());
    if (improved) setPrompt(improved);
  };

  const toggleRefId = (id: string) => {
    setSelectedRefIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const activeRefs = referencePhotos.filter((r) => selectedRefIds.includes(r.id));

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-border px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" className="shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <h1 className="text-lg font-semibold text-foreground">Vision Board</h1>
          <span className="text-xs text-muted-foreground">{images.length} images</span>
        </div>

        <div className="flex gap-2 items-center">
          {/* Reference Photos Button */}
          <TooltipProvider>
            <Popover open={refPopoverOpen} onOpenChange={setRefPopoverOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button className="relative flex items-center h-8">
                      {referencePhotos.length === 0 ? (
                        <div className="h-8 w-8 rounded-full border-2 border-dashed border-muted-foreground/40 hover:border-primary transition-colors flex items-center justify-center">
                          <User className="h-4 w-4 text-muted-foreground" />
                        </div>
                      ) : (
                        <div className="flex items-center -space-x-2">
                          {referencePhotos.slice(0, 3).map((ref, i) => (
                            <div
                              key={ref.id}
                              className="relative h-8 w-8 rounded-full overflow-hidden border-2 border-background ring-1 ring-border hover:ring-primary transition-all"
                              style={{ zIndex: referencePhotos.length - i }}
                            >
                              <img src={ref.url} alt={ref.label} className="w-full h-full object-cover" />
                              {selectedRefIds.includes(ref.id) && (
                                <div className="absolute inset-0 bg-primary/50 flex items-center justify-center">
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                              )}
                            </div>
                          ))}
                          {referencePhotos.length > 3 && (
                            <div className="h-8 w-8 rounded-full border-2 border-background bg-secondary flex items-center justify-center text-xs font-medium text-muted-foreground">
                              +{referencePhotos.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Indicator dot if any selected */}
                      {activeRefs.length > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-primary border border-background" />
                      )}
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {referencePhotos.length === 0
                      ? "Add reference photos for AI generations"
                      : `${referencePhotos.length} reference photo${referencePhotos.length > 1 ? "s" : ""} — click to manage`}
                  </p>
                </TooltipContent>
              </Tooltip>
              <PopoverContent align="end" className="p-0 w-auto" sideOffset={8}>
                <RefPhotosPopover
                  referencePhotos={referencePhotos}
                  selectedIds={selectedRefIds}
                  onToggle={toggleRefId}
                  onAdd={(file, label, kind) => addReferencePhoto(file, label, kind)}
                  onRemove={removeReferencePhoto}
                  onRenameStart={(ref) => { setRenamingRef(ref); setRefPopoverOpen(false); }}
                  onChangeKind={updateReferenceKind}
                />
              </PopoverContent>
            </Popover>
          </TooltipProvider>

          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setShowAIPrompt(!showAIPrompt)}
          >
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">Generate</span>
          </Button>
        </div>
      </div>

      {/* AI Prompt Bar */}
      <AnimatePresence>
        {showAIPrompt && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-b border-border overflow-hidden"
          >
            <div className="p-4 space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Describe your vision… e.g. 'A peaceful beachside home with a sunset view'"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
                  className="flex-1 bg-secondary/50"
                  disabled={isGenerating}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 shrink-0"
                  onClick={handleAssist}
                  disabled={!prompt.trim() || isAssisting || isGenerating}
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  {isAssisting ? "Improving…" : "Assist Me"}
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={!prompt.trim() || isGenerating}
                  className="cosmic-button shrink-0"
                >
                  {isGenerating ? "Creating…" : "Generate"}
                </Button>
              </div>

              {/* Active reference pills */}
              {referencePhotos.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Users className="h-3 w-3" /> Include:
                  </span>
                  {referencePhotos.map((ref) => {
                    const on = selectedRefIds.includes(ref.id);
                    const KindIcon = KIND_META[ref.kind].Icon;
                    return (
                      <button
                        key={ref.id}
                        onClick={() => toggleRefId(ref.id)}
                        className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 border text-xs transition-all ${
                          on
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-muted-foreground hover:border-muted-foreground"
                        }`}
                        title={`${KIND_META[ref.kind].label}: ${ref.label}`}
                      >
                        <img src={ref.url} alt={ref.label} className="h-4 w-4 rounded-full object-cover" />
                        <KindIcon className="h-3 w-3 opacity-70" />
                        {ref.label}
                        {on && <Check className="h-3 w-3" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {images.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div
              {...getRootProps()}
              className={`cosmic-card p-10 max-w-lg w-full text-center cursor-pointer transition-all duration-300 ${
                isDragActive ? "border-cosmic-400 scale-[1.02] shadow-[0_0_30px_-5px_hsl(var(--primary)/0.3)]" : ""
              }`}
            >
              <input {...getInputProps()} />
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-cosmic-400 to-cosmic-600 mb-6 shadow-lg">
                <ImagePlus className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Start Your Vision Board</h2>
              <p className="text-muted-foreground text-sm mb-6">
                Drag & drop images here, or click to browse. Upload photos of your dreams, goals, and the life you're manifesting.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button className="gap-2" disabled={isUploading}>
                  <Upload className="h-4 w-4" />
                  {isUploading ? "Uploading…" : "Upload Images"}
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={(e) => { e.stopPropagation(); setShowAIPrompt(true); }}
                >
                  <Sparkles className="h-4 w-4" /> Generate with AI
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div
              {...getRootProps()}
              className={`cosmic-card p-4 text-center cursor-pointer transition-all duration-300 ${
                isDragActive ? "border-cosmic-400 scale-[1.01]" : ""
              }`}
            >
              <input {...getInputProps()} />
              <p className="text-sm text-muted-foreground">
                <Upload className="inline h-4 w-4 mr-1 -mt-0.5" />
                {isDragActive ? "Drop images here…" : "Drag & drop or click to add more images"}
              </p>
            </div>

            <motion.div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" layout>
              <AnimatePresence>
                {images.map((img) => (
                  <motion.div
                    key={img.id}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="group relative aspect-square rounded-xl overflow-hidden cosmic-card cursor-pointer"
                    onClick={() => setPreviewIndex(images.findIndex((i) => i.id === img.id))}
                  >
                    {img.type === "video" ? (
                      <video
                        src={img.url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img src={img.url} alt={img.name} className="w-full h-full object-cover" loading="lazy" />
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {/* Action buttons — top right, only for non-video images */}
                      {img.type !== "video" && (
                        <div className="absolute top-2 right-2 flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                            title="Upscale & Fix Faces"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpscale(img.id, img.url, img.name);
                            }}
                            disabled={isUpscaling || isExpanding || isGenerating}
                          >
                            <ZoomIn className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                            title="Expand to 1920×1080 HD"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExpand(img.id, img.url, img.name);
                            }}
                            disabled={isExpanding || isUpscaling || isGenerating}
                          >
                            <Maximize2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                            title="Bring to Life"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBringToLifeImage({ id: img.id, url: img.url, name: img.name });
                            }}
                          >
                            <Video className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}

                      <div className="absolute bottom-2 left-2 right-2 flex items-end justify-between">
                        <span className="text-xs text-white/80 truncate mr-2">
                          {img.isAIGenerated && <Sparkles className="inline h-3 w-3 mr-1 -mt-0.5" />}
                          {img.name}
                        </span>
                        <div className="flex gap-1">
                          {img.isAIGenerated && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                              onClick={(e) => { e.stopPropagation(); setPrompt(img.prompt || img.name); setShowAIPrompt(true); }}
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-white/80 hover:text-destructive hover:bg-destructive/20"
                            onClick={(e) => { e.stopPropagation(); removeImage(img.id); }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
        )}
      </div>

      {/* Preview Lightbox */}
      <AnimatePresence>
        {previewItem && previewIndex !== null && (
          <motion.div
            key="preview-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 outline-none"
            onClick={() => setPreviewIndex(null)}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft" && previewIndex > 0) {
                e.stopPropagation();
                setPreviewIndex(previewIndex - 1);
              } else if (e.key === "ArrowRight" && previewIndex < images.length - 1) {
                e.stopPropagation();
                setPreviewIndex(previewIndex + 1);
              } else if (e.key === "Escape") {
                setPreviewIndex(null);
              }
            }}
            tabIndex={0}
            ref={(el) => el?.focus()}
          >
            {/* Close */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
              onClick={() => setPreviewIndex(null)}
            >
              <X className="h-6 w-6" />
            </Button>

            {/* Prev arrow */}
            {previewIndex > 0 && (
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
                onClick={(e) => { e.stopPropagation(); setPreviewIndex(previewIndex - 1); }}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}

            {/* Next arrow */}
            {previewIndex < images.length - 1 && (
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
                onClick={(e) => { e.stopPropagation(); setPreviewIndex(previewIndex + 1); }}
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}

            {/* Counter */}
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm z-10">
              {previewIndex + 1} / {images.length}
            </span>

            {previewItem.type === "video" ? (
              <video
                src={previewItem.url}
                autoPlay
                loop
                playsInline
                controls
                className="max-w-full max-h-full rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img src={previewItem.url} alt="Preview" className="max-w-full max-h-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bring to Life Modal */}
      <AnimatePresence>
        {bringToLifeImage && (
          <BringToLifeModal
            imageUrl={bringToLifeImage.url}
            imageName={bringToLifeImage.name}
            onClose={() => setBringToLifeImage(null)}
            onAnimate={handleAnimate}
            onSaveToBoard={(videoUrl, name) => saveVideo(videoUrl, name)}
            onSetChatBackground={(videoUrl) => {
              void saveSelectedChatBackground(videoUrl);
            }}
          />
        )}
      </AnimatePresence>

      {/* Rename Dialog */}
      <AnimatePresence>
        {renamingRef && (
          <RenameDialog
            ref={renamingRef}
            onSave={updateReferenceLabel}
            onClose={() => setRenamingRef(null)}
          />
        )}
      </AnimatePresence>

      {/* Image Expansion Pending Popup */}
      <AnimatePresence>
        {isExpanding && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-4 rounded-2xl border border-border bg-background/95 backdrop-blur-md px-5 py-4 shadow-2xl"
          >
            <div className="relative h-9 w-9 shrink-0">
              <div className="absolute inset-0 rounded-full border-2 border-muted" />
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin" />
              <Maximize2 className="absolute inset-0 m-auto h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Expanding to HD…</p>
              <p className="text-xs text-muted-foreground">Rendering at 1920 × 1080 — this may take a moment</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upscale Pending Popup */}
      <AnimatePresence>
        {isUpscaling && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-4 rounded-2xl border border-border bg-background/95 backdrop-blur-md px-5 py-4 shadow-2xl"
          >
            <div className="relative h-9 w-9 shrink-0">
              <div className="absolute inset-0 rounded-full border-2 border-muted" />
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin" />
              <ZoomIn className="absolute inset-0 m-auto h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Enhancing image…</p>
              <p className="text-xs text-muted-foreground">Upscaling & fixing faces — this may take a moment</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default VisionBoard;


